import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shell } from '../layout/Shell.js';
import { getPricing, updateFare, upsertSurge, type FareConfigRow, type SurgeRow } from '../api/admin.js';
import { useToast } from '../components/Toast.js';

function FareEditor({ row, onSave, busy }: { row: FareConfigRow; onSave: (next: FareConfigRow) => void; busy: boolean }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);

  const update = (key: keyof FareConfigRow, value: number) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <form
      className="card p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <p className="font-display text-lg font-semibold capitalize">{draft.vehicleType}</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Base</span>
          <input type="number" min="0" className="input num" value={draft.base} onChange={(e) => update('base', Number(e.target.value))} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Per km</span>
          <input type="number" min="0" className="input num" value={draft.perKm} onChange={(e) => update('perKm', Number(e.target.value))} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Per min</span>
          <input type="number" min="0" className="input num" value={draft.perMin} onChange={(e) => update('perMin', Number(e.target.value))} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Min fare</span>
          <input type="number" min="0" className="input num" value={draft.minFare} onChange={(e) => update('minFare', Number(e.target.value))} />
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function SurgeEditor({ row, onSave, busy }: { row: SurgeRow; onSave: (next: SurgeRow) => void; busy: boolean }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);

  return (
    <form
      className="card p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(draft);
      }}
    >
      <p className="font-display text-base font-semibold">{draft.name}</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Multiplier</span>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            className="input num"
            value={draft.multiplier}
            onChange={(e) => setDraft({ ...draft, multiplier: Number(e.target.value) })}
          />
        </label>
        <label className="flex items-center gap-2 self-end text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          />
          Active
        </label>
      </div>
      <div className="mt-3 flex justify-end">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

export function PricingPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const pricing = useQuery({ queryKey: ['admin-pricing'], queryFn: getPricing });

  const fareMutation = useMutation({
    mutationFn: updateFare,
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['admin-pricing'] });
      toast.show(`${row.vehicleType} fare updated.`, 'success');
    },
    onError: () => toast.show('Could not save fare changes.', 'error'),
  });
  const surgeMutation = useMutation({
    mutationFn: upsertSurge,
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['admin-pricing'] });
      toast.show(row.active ? `Surge active for ${row.name}.` : `Surge disabled for ${row.name}.`, 'info');
    },
    onError: () => toast.show('Could not save surge changes.', 'error'),
  });

  return (
    <Shell title="Pricing" subtitle="Per-vehicle fares and surge zones.">
      <section>
        <h2 className="mb-3 font-display text-lg font-semibold">Fare configuration</h2>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {pricing.data?.fares.map((f) => (
            <FareEditor
              key={f.vehicleType}
              row={f}
              busy={fareMutation.isPending}
              onSave={(next) => fareMutation.mutate(next)}
            />
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="mb-3 font-display text-lg font-semibold">Surge zones</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {pricing.data?.surge.map((s) => (
            <SurgeEditor
              key={s.id}
              row={s}
              busy={surgeMutation.isPending}
              onSave={(next) => surgeMutation.mutate(next)}
            />
          ))}
          {pricing.data?.surge.length === 0 ? (
            <p className="text-sm text-slate-500">No surge zones configured.</p>
          ) : null}
        </div>
      </section>
    </Shell>
  );
}
